import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';

export function SourcesPage() {
  return (
    <article className="max-w-3xl mx-auto px-4 pt-12 pb-20 sm:pt-16 aces-prose">
      <SEO
        title="Sources & Curriculum Alignment — ACES"
        description="How ACES content is authored, verified, and aligned to public CBSE NCERT and Kerala SCERT Higher Secondary commerce syllabi."
        canonical="https://acescommerce.com/sources/"
      />
      <h1>Sources &amp; Curriculum Alignment</h1>
      <p>
        ACES is built around the public Higher Secondary commerce syllabi of CBSE (NCERT-aligned) and Kerala&rsquo;s
        Higher Secondary curriculum (run by the Directorate of Higher Secondary Education, DHSE, with textbooks from SCERT Kerala). We treat their syllabi as <em>specifications</em> —
        what topics to cover, at what depth, in what order — and write all chapter content independently from
        scratch.
      </p>

      <h2>What we use from public sources</h2>
      <ul>
        <li>
          <strong>Chapter titles, unit names, concept lists</strong> — these come from the published CBSE Class
          11/12 commerce syllabi (Accountancy, Business Studies, Economics) and the Kerala SCERT HSE syllabi.
          These are public curriculum specifications.
        </li>
        <li>
          <strong>Statutory facts</strong> — the Companies Act 2013, GST Act, Consumer Protection Act 2019,
          RBI/SEBI functions, AS-3 cash-flow rules, Schedule III balance-sheet format. These are public laws and
          accounting standards.
        </li>
        <li>
          <strong>Indian business context references</strong> — public-domain factual references to companies
          (Reliance Industries, TCS, ITC, etc.), regulators, and government schemes. Used as illustrative
          examples, never claiming endorsement.
        </li>
      </ul>

      <h2>What we do NOT use</h2>
      <ul>
        <li>NCERT textbook prose, worked examples, or exercise questions — these are independently authored.</li>
        <li>Content scraped from competitor platforms (Byju&rsquo;s, Vedantu, Toppr, Physics Wallah, Khan Academy India, Embibe).</li>
        <li>Trademarked logos, brand artwork, or copyrighted images from any textbook publisher.</li>
      </ul>

      <h2>How chapters get verified</h2>
      <p>
        Every chapter passes through a multi-stage authoring + verification pipeline. If a chapter is still being
        re-checked, it carries an &ldquo;Under review&rdquo; badge, which means a verifier has flagged something
        specific for a second look.
      </p>
      <ol>
        <li>
          <strong>Author pass</strong> — narrative, worked example, practice questions, and graduated hints
          generated and written for the chapter.
        </li>
        <li>
          <strong>Verifier pass</strong> — checks factual accuracy, class-level appropriateness, syllabus
          alignment, and arithmetic correctness in practice questions.
        </li>
        <li>
          <strong>Fixer pass</strong> — chapters flagged with critical issues go through a targeted rewrite
          pipeline focused on the specific issues raised.
        </li>
        <li>
          <strong>Continuous human review</strong> — when learners or teachers email us a correction (see{' '}
          <Link to="/contact/" className="text-accent-600 underline">Contact</Link>), we update the chapter and
          credit the contributor if they wish.
        </li>
      </ol>

      <h2>Interactive simulations</h2>
      <p>
        The Journal &amp; Ledger simulation and all other ACES interactive simulations are original ACES
        designs. Concepts they exercise (debit/credit rules, double-entry, ratio analysis, working capital) are
        universal accounting practice — not anyone&rsquo;s copyright.
      </p>

      <h2>Reporting an error</h2>
      <p>
        Found an arithmetic mistake, a factual slip, or a definition that&rsquo;s drifted off-syllabus? Send the
        chapter URL plus a short note to{' '}
        <a href="mailto:hello@acescommerce.com" className="text-accent-600 underline">hello@acescommerce.com</a>.
        We fix things fast.
      </p>

      <p className="text-sm text-ink-500 mt-8">
        ACES is an independent learning platform. We are not affiliated with NCERT, CBSE, the Government of
        Kerala, or any school board. See our <Link to="/about/" className="text-accent-600 underline">About</Link>{' '}
        page for more.
      </p>
    </article>
  );
}
